/**
 * Venue Dashboard
 * Venue-owner payout home: Stripe Connect onboarding, earnings balance and
 * ledger, the owner's venues, and the classes hosted at them.
 *
 * Mirrors the creator dashboard's Earnings tab but reads from /api/venue/*
 * (payee_type='venue_owner'). Accessible to the 'venue' role (and admins,
 * for support).
 */
import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CALENDAR_CATEGORIES } from '@shared/schema';
import { Building2, CalendarDays, DollarSign, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import { authedFetch, authedJson } from '@/lib/http';
import { formatDateCompact } from '@/lib/dateUtils';
import { PublicShell } from '@/components/public-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CATEGORY_LABELS } from '@/components/calendar/entry-card';

interface VenueConnectStatus {
  connected: boolean;
  onboardingStatus: string;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
}

interface VenueSummary {
  id: string;
  name: string;
  slug: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  description: string | null;
  website: string | null;
  phoneNumber: string | null;
  email: string | null;
  hours: string | null;
  thumbnailUrl: string | null;
  socialLinks: { instagram?: string; facebook?: string } | null;
  isActive: boolean;
}

interface VenueEarnings {
  balanceCents: number;
  lifetimeEarnedCents: number;
  entries: Array<{
    id: string;
    entryType: string;
    amountCents: number;
    orderId: string | null;
    referenceNote: string | null;
    createdAt: string;
    purchaserName: string | null;
    purchaserEmail: string | null;
    itemTitle: string | null;
    sessionStartDateTime: string | null;
    quantity: number | null;
    orderGrossAmountCents: number | null;
  }>;
}

interface HostedClass {
  id: string;
  title: string;
  isPublished: boolean;
  isArchived: boolean;
  venueId: string | null;
  price: string;
}

interface VenueEvent {
  id: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string | null;
  venueId: string | null;
  category: string;
  attendanceMode: string;
  eventStatus: string;
  isPublished: boolean;
  externalUrl: string | null;
}

const BLANK_VENUE_EDIT_FORM = {
  address: '',
  city: '',
  state: '',
  zip: '',
  description: '',
  website: '',
  phoneNumber: '',
  email: '',
  hours: '',
  instagram: '',
  facebook: '',
};

const BLANK_EVENT_FORM = {
  venueId: '',
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  category: 'community',
  attendanceMode: 'in_person',
  externalUrl: '',
};

// Local datetime string for a <input type="datetime-local"> from a UTC ISO string.
function utcToLocalDatetimeString(utcDateString: string): string {
  const date = new Date(utcDateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export default function VenueDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();

  const isVenueUser = !!user && (user.role === 'venue' || user.role === 'admin');

  // Handle Stripe Connect return (?connect=success|refresh)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get('connect');
    if (connectParam === 'success') {
      toast({
        title: 'Payout setup complete',
        description:
          'Your Stripe account is connected. Venue earnings will be transferred after the holding period.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/venue/connect/status'] });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (connectParam === 'refresh') {
      toast({
        title: 'Stripe onboarding session expired',
        description: 'Please click "Set up payouts" to restart your onboarding.',
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const { data: connectStatus } = useQuery<VenueConnectStatus>({
    queryKey: ['/api/venue/connect/status'],
    enabled: isVenueUser,
    queryFn: async () => authedJson<VenueConnectStatus>('/api/venue/connect/status'),
  });

  const { data: venues, isLoading: venuesLoading } = useQuery<VenueSummary[]>({
    queryKey: ['/api/venue/me'],
    enabled: isVenueUser,
    queryFn: async () => authedJson<VenueSummary[]>('/api/venue/me'),
  });

  const { data: earnings, isLoading: earningsLoading } = useQuery<VenueEarnings>({
    queryKey: ['/api/venue/earnings'],
    enabled: isVenueUser,
    queryFn: async () => authedJson<VenueEarnings>('/api/venue/earnings'),
  });

  const { data: hostedClasses, isLoading: classesLoading } = useQuery<HostedClass[]>({
    queryKey: ['/api/venue/classes'],
    enabled: isVenueUser,
    queryFn: async () => authedJson<HostedClass[]>('/api/venue/classes'),
  });

  // ─── Venue location editing ──────────────────────────────────────────────
  const [editingVenueId, setEditingVenueId] = useState<string | null>(null);
  const [venueForm, setVenueForm] = useState(BLANK_VENUE_EDIT_FORM);

  function startEditingVenue(venue: VenueSummary) {
    setEditingVenueId(venue.id);
    setVenueForm({
      address: venue.address ?? '',
      city: venue.city ?? '',
      state: venue.state ?? '',
      zip: venue.zip ?? '',
      description: venue.description ?? '',
      website: venue.website ?? '',
      phoneNumber: venue.phoneNumber ?? '',
      email: venue.email ?? '',
      hours: venue.hours ?? '',
      instagram: venue.socialLinks?.instagram ?? '',
      facebook: venue.socialLinks?.facebook ?? '',
    });
  }

  const updateVenueMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof venueForm }) => {
      const body = {
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        description: data.description || null,
        website: data.website || null,
        phoneNumber: data.phoneNumber || null,
        email: data.email || null,
        hours: data.hours || null,
        socialLinks:
          data.instagram || data.facebook
            ? { instagram: data.instagram || undefined, facebook: data.facebook || undefined }
            : null,
      };
      const res = await authedFetch(`/api/venue/locations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update venue');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Venue updated', description: 'Your changes are now live on your page.' });
      setEditingVenueId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/venue/me'] });
    },
    onError: error => {
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // ─── Venue calendar events ────────────────────────────────────────────────
  const { data: eventsResponse, isLoading: eventsLoading } = useQuery<{ data: VenueEvent[] }>({
    queryKey: ['/api/venue/events'],
    enabled: isVenueUser,
    queryFn: async () => authedJson<{ data: VenueEvent[] }>('/api/venue/events'),
  });
  const events = eventsResponse?.data ?? [];

  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [eventForm, setEventForm] = useState(BLANK_EVENT_FORM);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  function openAddEvent() {
    setEditingEventId(null);
    setEventForm({ ...BLANK_EVENT_FORM, venueId: venues?.[0]?.id ?? '' });
    setEventFormOpen(true);
  }

  function openEditEvent(ev: VenueEvent) {
    setEditingEventId(ev.id);
    setEventForm({
      venueId: ev.venueId ?? '',
      title: ev.title,
      description: ev.description ?? '',
      startsAt: utcToLocalDatetimeString(ev.startsAt),
      endsAt: ev.endsAt ? utcToLocalDatetimeString(ev.endsAt) : '',
      category: ev.category,
      attendanceMode: ev.attendanceMode,
      externalUrl: ev.externalUrl ?? '',
    });
    setEventFormOpen(true);
  }

  const saveEventMutation = useMutation({
    mutationFn: async (data: typeof eventForm) => {
      const url = editingEventId ? `/api/venue/events/${editingEventId}` : '/api/venue/events';
      const method = editingEventId ? 'PUT' : 'POST';
      const body = {
        venueId: data.venueId,
        title: data.title,
        description: data.description || null,
        startsAt: data.startsAt ? new Date(data.startsAt).toISOString() : undefined,
        endsAt: data.endsAt ? new Date(data.endsAt).toISOString() : null,
        category: data.category,
        attendanceMode: data.attendanceMode,
        externalUrl: data.externalUrl || null,
      };
      const res = await authedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save event');
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: editingEventId ? 'Event updated' : 'Event posted',
        description: 'It now shows on your location page and the calendar.',
      });
      setEventFormOpen(false);
      setEditingEventId(null);
      setEventForm(BLANK_EVENT_FORM);
      queryClient.invalidateQueries({ queryKey: ['/api/venue/events'] });
    },
    onError: error => {
      toast({
        title: 'Save failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authedFetch(`/api/venue/events/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove event');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Event removed' });
      setDeleteConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ['/api/venue/events'] });
    },
    onError: () => {
      toast({
        title: 'Failed to remove event',
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const connectOnboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/venue/connect/onboard', { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || 'Failed to start Stripe onboarding.'
        );
      }
      return (await response.json()) as { url: string };
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to start Stripe onboarding. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const connectDashboardMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/venue/connect/dashboard', { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to open Stripe settings.');
      }
      return (await response.json()) as { url: string };
    },
    onSuccess: data => {
      window.location.href = data.url;
    },
    onError: error => {
      toast({
        title: 'Stripe settings unavailable',
        description:
          error instanceof Error
            ? error.message
            : 'Unable to open Stripe settings. Please try again.',
        variant: 'destructive',
      });
    },
  });

  if (!isVenueUser) {
    return (
      <PublicShell routeKey="venue-dashboard">
        <div className="page-shell py-8 text-center">
          <h1 className="text-2xl font-bold mb-2">Venue Dashboard</h1>
          <p className="text-muted-foreground">
            This page is for venue owners. If you host Kalovate classes at your space, contact us to
            get set up.
          </p>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell routeKey="venue-dashboard">
      <div className="page-shell py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="venue-dashboard-title">
            Venue Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your venue earnings and manage your payout account.
          </p>
        </div>

        {/* Connect status */}
        {connectStatus && (
          <Card className={connectStatus.chargesEnabled ? 'border-green-300' : 'border-orange-300'}>
            <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <DollarSign
                  size={20}
                  className={
                    connectStatus.chargesEnabled
                      ? 'text-green-600 shrink-0'
                      : 'text-orange-600 shrink-0'
                  }
                />
                <div className="min-w-0">
                  <p className="font-medium text-sm">
                    {connectStatus.chargesEnabled
                      ? 'Payouts active'
                      : connectStatus.onboardingStatus === 'in_progress'
                        ? 'Payout setup in progress'
                        : 'Payout account not connected'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {connectStatus.chargesEnabled
                      ? 'Your venue earnings will be transferred after the holding period.'
                      : 'Complete Stripe onboarding to receive your venue earnings.'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                {!connectStatus.chargesEnabled && (
                  <Button
                    size="sm"
                    onClick={() => connectOnboardMutation.mutate()}
                    disabled={connectOnboardMutation.isPending}
                    data-testid="button-venue-connect-onboard"
                  >
                    {connectOnboardMutation.isPending
                      ? 'Redirecting…'
                      : connectStatus.onboardingStatus === 'in_progress'
                        ? 'Continue setup'
                        : 'Set up payouts'}
                  </Button>
                )}
                {connectStatus.connected && (
                  <Button
                    size="sm"
                    variant={connectStatus.chargesEnabled ? 'default' : 'outline'}
                    onClick={() => connectDashboardMutation.mutate()}
                    disabled={connectDashboardMutation.isPending}
                    data-testid="button-venue-connect-dashboard"
                  >
                    {connectDashboardMutation.isPending ? 'Opening…' : 'Edit Stripe settings'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Earnings summary */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Current Balance</CardDescription>
            </CardHeader>
            <CardContent>
              {earningsLoading ? (
                <div className="h-8 w-24 bg-muted animate-pulse rounded" />
              ) : (
                <p className="text-2xl font-bold" data-testid="venue-balance">
                  ${((earnings?.balanceCents ?? 0) / 100).toFixed(2)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">After payouts &amp; refunds</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Lifetime Earned</CardDescription>
            </CardHeader>
            <CardContent>
              {earningsLoading ? (
                <div className="h-8 w-24 bg-muted animate-pulse rounded" />
              ) : (
                <p className="text-2xl font-bold" data-testid="venue-lifetime-earned">
                  ${((earnings?.lifetimeEarnedCents ?? 0) / 100).toFixed(2)}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">Gross venue share</p>
            </CardContent>
          </Card>
        </div>

        {/* My venues */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 size={18} />
              My Venues
            </CardTitle>
            <CardDescription>Spaces you own that host Kalovate classes</CardDescription>
          </CardHeader>
          <CardContent>
            {venuesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : venues && venues.length > 0 ? (
              <div className="space-y-3">
                {venues.map(venue =>
                  editingVenueId === venue.id ? (
                    <div
                      key={venue.id}
                      className="border rounded-lg p-4 space-y-3"
                      data-testid={`venue-edit-form-${venue.id}`}
                    >
                      <p className="font-medium">{venue.name}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`address-${venue.id}`}>Address</Label>
                          <Input
                            id={`address-${venue.id}`}
                            value={venueForm.address}
                            onChange={e => setVenueForm(f => ({ ...f, address: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1.5 col-span-1">
                            <Label htmlFor={`city-${venue.id}`}>City</Label>
                            <Input
                              id={`city-${venue.id}`}
                              value={venueForm.city}
                              onChange={e => setVenueForm(f => ({ ...f, city: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`state-${venue.id}`}>State</Label>
                            <Input
                              id={`state-${venue.id}`}
                              value={venueForm.state}
                              onChange={e => setVenueForm(f => ({ ...f, state: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`zip-${venue.id}`}>Zip</Label>
                            <Input
                              id={`zip-${venue.id}`}
                              value={venueForm.zip}
                              onChange={e => setVenueForm(f => ({ ...f, zip: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`phone-${venue.id}`}>Phone</Label>
                          <Input
                            id={`phone-${venue.id}`}
                            value={venueForm.phoneNumber}
                            onChange={e =>
                              setVenueForm(f => ({ ...f, phoneNumber: e.target.value }))
                            }
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`email-${venue.id}`}>Public contact email</Label>
                          <Input
                            id={`email-${venue.id}`}
                            type="email"
                            value={venueForm.email}
                            onChange={e => setVenueForm(f => ({ ...f, email: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`website-${venue.id}`}>Website</Label>
                          <Input
                            id={`website-${venue.id}`}
                            value={venueForm.website}
                            onChange={e => setVenueForm(f => ({ ...f, website: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`instagram-${venue.id}`}>Instagram URL</Label>
                          <Input
                            id={`instagram-${venue.id}`}
                            value={venueForm.instagram}
                            onChange={e => setVenueForm(f => ({ ...f, instagram: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`facebook-${venue.id}`}>Facebook URL</Label>
                          <Input
                            id={`facebook-${venue.id}`}
                            value={venueForm.facebook}
                            onChange={e => setVenueForm(f => ({ ...f, facebook: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`hours-${venue.id}`}>Hours</Label>
                        <Textarea
                          id={`hours-${venue.id}`}
                          rows={2}
                          placeholder={'e.g. Tue-Sat 10am-6pm'}
                          value={venueForm.hours}
                          onChange={e => setVenueForm(f => ({ ...f, hours: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`description-${venue.id}`}>About this space</Label>
                        <Textarea
                          id={`description-${venue.id}`}
                          rows={4}
                          value={venueForm.description}
                          onChange={e => setVenueForm(f => ({ ...f, description: e.target.value }))}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            updateVenueMutation.mutate({ id: venue.id, data: venueForm })
                          }
                          disabled={updateVenueMutation.isPending}
                        >
                          {updateVenueMutation.isPending ? 'Saving…' : 'Save changes'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingVenueId(null)}
                          disabled={updateVenueMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={venue.id}
                      className="flex flex-col gap-2 border rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between"
                      data-testid={`venue-${venue.id}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium break-words">{venue.name}</p>
                        {(venue.address || venue.city) && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin size={12} className="shrink-0" />
                            <span className="break-words">
                              {[venue.address, venue.city, venue.state].filter(Boolean).join(', ')}
                            </span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={venue.isActive ? 'default' : 'secondary'}>
                          {venue.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditingVenue(venue)}
                          data-testid={`button-edit-venue-${venue.id}`}
                        >
                          <Pencil size={14} className="mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No venues assigned to your account yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Kalovate links your venues to your account — contact us if something's missing.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hosted classes */}
        <Card>
          <CardHeader>
            <CardTitle>Classes at Your Venues</CardTitle>
            <CardDescription>Classes currently scheduled at your spaces</CardDescription>
          </CardHeader>
          <CardContent>
            {classesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : hostedClasses && hostedClasses.length > 0 ? (
              <div className="space-y-2">
                {hostedClasses.map(cls => (
                  <div
                    key={cls.id}
                    className="flex items-center justify-between gap-3 border rounded-lg p-3"
                    data-testid={`hosted-class-${cls.id}`}
                  >
                    <p className="font-medium min-w-0 break-words">{cls.title}</p>
                    <div className="flex gap-2 shrink-0">
                      <Badge variant="outline">${cls.price}</Badge>
                      {cls.isArchived ? (
                        <Badge variant="secondary">Archived</Badge>
                      ) : cls.isPublished ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          Published
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          Draft
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No classes hosted at your venues yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My calendar events (separate from Kalovate classes) */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays size={18} />
                My Events
              </CardTitle>
              <CardDescription>
                Calendar items at your space — not Kalovate classes, e.g. open mic nights or
                community meetups
              </CardDescription>
            </div>
            {venues && venues.length > 0 && !eventFormOpen && (
              <Button size="sm" onClick={openAddEvent} data-testid="button-add-venue-event">
                <Plus size={14} className="mr-1" />
                Add Event
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {eventFormOpen && (
              <div className="border rounded-lg p-4 space-y-3">
                {venues && venues.length > 1 && (
                  <div className="space-y-1.5">
                    <Label>Venue</Label>
                    <Select
                      value={eventForm.venueId}
                      onValueChange={v => setEventForm(f => ({ ...f, venueId: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map(v => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="event-title">Title</Label>
                  <Input
                    id="event-title"
                    value={eventForm.title}
                    onChange={e => setEventForm(f => ({ ...f, title: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="event-starts">Starts</Label>
                    <Input
                      id="event-starts"
                      type="datetime-local"
                      value={eventForm.startsAt}
                      onChange={e => setEventForm(f => ({ ...f, startsAt: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="event-ends">Ends</Label>
                    <Input
                      id="event-ends"
                      type="datetime-local"
                      value={eventForm.endsAt}
                      onChange={e => setEventForm(f => ({ ...f, endsAt: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Category</Label>
                    <Select
                      value={eventForm.category}
                      onValueChange={v => setEventForm(f => ({ ...f, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CALENDAR_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {CATEGORY_LABELS[cat] ?? cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Attendance</Label>
                    <Select
                      value={eventForm.attendanceMode}
                      onValueChange={v => setEventForm(f => ({ ...f, attendanceMode: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_person">In person</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event-description">Description</Label>
                  <Textarea
                    id="event-description"
                    rows={3}
                    value={eventForm.description}
                    onChange={e => setEventForm(f => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="event-url">External link (optional)</Label>
                  <Input
                    id="event-url"
                    value={eventForm.externalUrl}
                    onChange={e => setEventForm(f => ({ ...f, externalUrl: e.target.value }))}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => saveEventMutation.mutate(eventForm)}
                    disabled={
                      saveEventMutation.isPending || !eventForm.title || !eventForm.startsAt
                    }
                  >
                    {saveEventMutation.isPending
                      ? 'Saving…'
                      : editingEventId
                        ? 'Save changes'
                        : 'Post event'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEventFormOpen(false);
                      setEditingEventId(null);
                    }}
                    disabled={saveEventMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {eventsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : events.length > 0 ? (
              <div className="space-y-2">
                {events.map(ev => (
                  <div
                    key={ev.id}
                    className="flex items-center justify-between gap-3 border rounded-lg p-3"
                    data-testid={`venue-event-${ev.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium break-words">{ev.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateCompact(ev.startsAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {ev.eventStatus === 'cancelled' ? (
                        <Badge variant="secondary">Removed</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          Posted
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditEvent(ev)}
                        data-testid={`button-edit-event-${ev.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      {deleteConfirmId === ev.id ? (
                        <>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteEventMutation.mutate(ev.id)}
                            disabled={deleteEventMutation.isPending}
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirmId(ev.id)}
                          data-testid={`button-remove-event-${ev.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No events posted at your venues yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transaction history */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
            <CardDescription>Most recent 50 ledger entries</CardDescription>
          </CardHeader>
          <CardContent>
            {earningsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : earnings && earnings.entries.length > 0 ? (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 pr-4 font-medium">Date</th>
                      <th className="text-left py-2 pr-4 font-medium">Type</th>
                      <th className="text-left py-2 pr-4 font-medium">Purchaser</th>
                      <th className="text-left py-2 pr-4 font-medium">Item</th>
                      <th className="text-right py-2 pr-4 font-medium">Qty</th>
                      <th className="text-left py-2 pr-4 font-medium">Reference</th>
                      <th className="text-right py-2 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.entries.map(entry => {
                      const amountDollars = entry.amountCents / 100;
                      const isNegative = amountDollars < 0;
                      return (
                        <tr key={entry.id} className="border-b last:border-0">
                          <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                            {formatDateCompact(entry.createdAt)}
                          </td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline" className="capitalize text-xs">
                              {entry.entryType.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4 max-w-[180px]">
                            {entry.purchaserName ? (
                              <div className="min-w-0">
                                <p className="truncate font-medium">{entry.purchaserName}</p>
                                {entry.purchaserEmail && (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {entry.purchaserEmail}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 max-w-[200px]">
                            {entry.itemTitle ? (
                              <div className="min-w-0">
                                <p className="truncate">{entry.itemTitle}</p>
                                {entry.sessionStartDateTime && (
                                  <p className="truncate text-xs text-muted-foreground">
                                    {formatDateCompact(entry.sessionStartDateTime)}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                            {entry.quantity ?? '—'}
                          </td>
                          <td className="py-2 pr-4 max-w-[160px] truncate text-muted-foreground">
                            {entry.referenceNote ?? entry.orderId ?? '—'}
                          </td>
                          <td className="py-2 text-right">
                            <p
                              className={`font-medium tabular-nums ${isNegative ? 'text-destructive' : 'text-foreground'}`}
                            >
                              {isNegative ? '-' : '+'}${Math.abs(amountDollars).toFixed(2)}
                            </p>
                            {typeof entry.orderGrossAmountCents === 'number' &&
                              entry.orderGrossAmountCents !== Math.abs(entry.amountCents) && (
                                <p className="text-xs text-muted-foreground tabular-nums">
                                  of ${(entry.orderGrossAmountCents / 100).toFixed(2)} gross
                                </p>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No transactions yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Your share of class sales at your venues will appear here.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicShell>
  );
}
